<template>
  <v-container fluid class="mt-16 px-6">
    <div>
      <v-card flat>
        <v-card-title class="d-flex">
          <h2 v-if="!isPhone">Units</h2>
          <v-spacer></v-spacer>
          <add-unit-modal ref="addUnitModal" />
          <v-btn
            color=""
            class=""
            icon
            x-large
            @click="refresh"
            :loading="loading"
            ><v-icon>mdi-refresh</v-icon></v-btn
          >
        </v-card-title>
        <v-tabs v-model="activeTab">
          <v-tab>
            <v-icon left small>mdi-laptop</v-icon>
            <span class="text-none">Active Units</span>
          </v-tab>
          <v-tab>
            <v-icon left small>mdi-laptop-off</v-icon>
            <span class="text-none">Deleted Units</span>
          </v-tab>
        </v-tabs>
        <v-tabs-items v-model="activeTab">
          <v-tab-item>
            <v-card-text>
              <v-text-field
                prepend-icon="mdi-magnify"
                label="Search"
                v-model="search"
              ></v-text-field>

              <!-- Mobile: card list -->
              <div v-if="isPhone">
                <mobile-record-card
                  v-for="item in $mobileSearch(all_units, search)"
                  :key="item.id"
                  icon="mdi-desktop-tower-monitor"
                  :title="item.unit_model || 'Unit #' + item.id"
                  :subtitle="item.customer ? item.customer.customer_name : ''"
                  :fields="[
                    { label: 'Type', value: item.unit_type },
                    { label: 'Brand', value: item.unit_brand },
                    { label: 'Serial', value: item.serial_no },
                    { label: 'ID', value: item.id },
                  ]"
                >
                  <template #actions>
                    <v-btn
                      icon
                      color="primary"
                      @click="show_modal(['problem_modal', item])"
                      ><v-icon>mdi-list-box</v-icon></v-btn
                    >
                    <v-btn
                      icon
                      color="warning"
                      @click="show_modal(['update_unit_modal', item])"
                      ><v-icon>mdi-pencil</v-icon></v-btn
                    >
                    <v-btn
                      icon
                      color="success"
                      @click="show_modal(['summary_modal', item])"
                      ><v-icon>mdi-note-text-outline</v-icon></v-btn
                    >
                    <v-btn
                      icon
                      color="red"
                      @click="del(item)"
                      :loading="loading"
                      v-if="can('units.delete')"
                      ><v-icon>mdi-delete</v-icon></v-btn
                    >
                  </template>
                </mobile-record-card>
                <div
                  v-if="!$mobileSearch(all_units, search).length"
                  class="text-center text--secondary py-8"
                >
                  No units found.
                </div>
              </div>
              <!-- Desktop: data table -->
              <v-data-table
                v-else
                :loading="loading"
                :search="search"
                :items="all_units"
                :headers="headers"
              >
                <template v-slot:item.actions="{ item }">
                  <div class="d-flex">
                    <v-btn
                      @click="show_modal(['problem_modal', item])"
                      color="primary"
                      small
                      icon
                      class="mt-1"
                      ><v-icon>mdi-list-box</v-icon></v-btn
                    >
                    <v-btn
                      class="ml-2"
                      color="warning"
                      @click="show_modal(['update_unit_modal', item])"
                      icon
                    >
                      <v-icon class="mr-2">mdi-pencil</v-icon></v-btn
                    >
                    <v-btn
                      small
                      class=""
                      color="success"
                      @click="show_modal(['summary_modal', item])"
                      icon
                    >
                      <v-icon class="">mdi-note-text-outline</v-icon></v-btn
                    >

                    <v-btn
                      icon
                      color="red"
                      @click="del(item)"
                      :loading="loading"
                      v-if="can('units.delete')"
                      ><v-icon>mdi-delete</v-icon>
                    </v-btn>
                  </div>
                </template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>
          <v-tab-item>
            <v-card-text>
              <v-text-field
                prepend-icon="mdi-magnify"
                label="Search"
                v-model="deletedSearch"
              ></v-text-field>

              <!-- Mobile: card list -->
              <div v-if="isPhone">
                <mobile-record-card
                  v-for="item in $mobileSearch(deleted_units, deletedSearch)"
                  :key="item.id"
                  icon="mdi-laptop-off"
                  icon-color="grey"
                  :title="item.unit_model || 'Unit #' + item.id"
                  :subtitle="item.customer ? item.customer.customer_name : ''"
                  :fields="[
                    { label: 'Type', value: item.unit_type },
                    { label: 'Brand', value: item.unit_brand },
                    { label: 'Serial', value: item.serial_no },
                    { label: 'Deleted', value: formatted_date(item.deleted_at) },
                  ]"
                >
                  <template #actions>
                    <v-btn icon color="success" @click="restore(item)">
                      <v-icon>mdi-restore</v-icon>
                    </v-btn>
                  </template>
                </mobile-record-card>
                <div
                  v-if="!$mobileSearch(deleted_units, deletedSearch).length"
                  class="text-center text--secondary py-8"
                >
                  No deleted units.
                </div>
              </div>
              <!-- Desktop: data table -->
              <v-data-table
                v-else
                :loading="loadingDeleted"
                :search="deletedSearch"
                :items="deleted_units"
                :headers="deletedHeaders"
              >
                <template v-slot:item.deleted_at="{ item }">
                  {{ formatted_date(item.deleted_at) }}
                </template>
                <template v-slot:item.actions="{ item }">
                  <v-btn
                    icon
                    color="success"
                    @click="restore(item)"
                    title="Restore"
                  >
                    <v-icon>mdi-restore</v-icon>
                  </v-btn>
                </template>
              </v-data-table>
            </v-card-text>
          </v-tab-item>
        </v-tabs-items>

        <summary-modal
          v-if="show_summary_modal"
          :customer_id="selected_item.customer.id"
          :problems="selected_item.problems"
          @close="show_summary_modal = false"
        />

        <update_unit_modal
          :unit="selected_item"
          v-if="show_update_unit_modal"
          @close="show_update_unit_modal = false"
        />

        <problems-modal
          :item="selected_item"
          v-if="show_problems_modal"
          @close="show_problems_modal = false"
        />
      </v-card>
    </div>

    <!-- Mobile: floating Add Unit -->
    <v-btn
      v-if="isPhone && activeTab === 0"
      fab
      fixed
      bottom
      right
      color="primary"
      class="mobile-fab"
      @click="$refs.addUnitModal.dialog = true"
    >
      <v-icon>mdi-plus</v-icon>
    </v-btn>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from "vuex";
// import { defineAsyncComponent } from "vue";
import SummaryModal from "../components/Problems/summaryModal.vue";
import AddUnitModal from "../components/Unit/addUnitModal.vue";
import moment from "moment";

export default {
  components: {
    problemsModal: () => import("@/components/Problems/problemsModal.vue"),
    update_unit_modal: () => import("@/components/Unit/updateUnitModal.vue"),
    AddUnitModal,
    SummaryModal,
  },
  data() {
    return {
      selected_item: [],
      show_problems_modal: false,
      show_update_unit_modal: false,
      show_summary_modal: false,

      units: [],
      activeTab: 0,
      loading: false,
      loadingDeleted: false,
      search: "",
      deletedSearch: "",
      ready: false,
      headers: [
        { text: "", value: "actions" },
        { text: "ID", value: "id" },
        { text: "Customer Name", value: "customer.customer_name" },
        { text: "Unit Model", value: "unit_model" },
        { text: "Unit Type", value: "unit_type" },
        { text: "Unit Brand", value: "unit_brand" },
        { text: "Serial Number", value: "serial_no" },
      ],
      deletedHeaders: [
        { text: "", value: "actions" },
        { text: "ID", value: "id" },
        { text: "Customer Name", value: "customer.customer_name" },
        { text: "Unit Model", value: "unit_model" },
        { text: "Unit Type", value: "unit_type" },
        { text: "Unit Brand", value: "unit_brand" },
        { text: "Serial Number", value: "serial_no" },
        { text: "Deleted At", value: "deleted_at" },
      ],
    };
  },
  computed: {
    ...mapGetters({
      all_units: "unit/all_units",
      deleted_units: "unit/deleted_units",
      user: "auth/user",
      hasPermission: "permission/hasPermission",
      user_permissions: "permission/user_permissions",
    }),
  },
  methods: {
    // Hat-based permission gate; accounts without hats get no access.
    can(key) {
      return this.hasPermission(key);
    },
    show_modal(request) {
      if (request[0] == "update_unit_modal") this.show_update_unit_modal = true;
      if (request[0] == "problem_modal") this.show_problems_modal = true;
      if (request[0] == "summary_modal") this.show_summary_modal = true;
      this.selected_item = request[1];
    },
    ...mapActions({
      get_all_units: "unit/get_all_units",
      delete_unit: "unit/delete_unit",
      get_deleted_units: "unit/get_deleted_units",
      restore_unit: "unit/restore_unit",
    }),
    formatted_date(date) {
      return moment(date).format("LL");
    },
    async del(item) {
      let x = await this.$confirm({
        title: "Delete Unit",
        message:
          "Are you sure you want to delete this unit? It will be moved to the Deleted Units tab and can be restored later.",
        color: "red",
        icon: "mdi-delete",
      });
      if (x) {
        this.delete_unit(item).then(() => {
          this.fetch_deleted();
          this.$alert({
            title: "Success",
            message: "Successfully deleted a unit",
            color: "success",
            icon: "mdi-check-circle",
          });
        });
      }
    },
    async restore(item) {
      if (!this.can("units.restore")) {
        this.$alert({
          title: "Error",
          message: "You are not allowed to do this.",
          color: "error",
          icon: "mdi-alert-circle",
        });
        return;
      }
      let x = await this.$confirm({
        title: "Restore Unit",
        message: "Are you sure you want to restore this unit?",
        color: "success",
        icon: "mdi-restore",
      });
      if (x) {
        this.restore_unit(item).then(() => {
          this.$alert({
            title: "Success",
            message: "Successfully restored a unit",
            color: "success",
            icon: "mdi-check-circle",
          });
        });
      }
    },
    async fetch_deleted() {
      this.loadingDeleted = true;
      try {
        await this.get_deleted_units();
      } finally {
        this.loadingDeleted = false;
      }
    },
    refresh() {
      this.loading = true;
      this.get_all_units().then(() => {
        this.loading = false;
      });
      this.fetch_deleted();
    },
  },
  created() {
    if (this.all_units.length <= 0) {
      this.loading = true;
      this.get_all_units().then(() => {
        this.loading = false;
      });
    }
    this.fetch_deleted();
  },
};
</script>

<style></style>
